import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { memoize } from 'lodash';
import {
  ProvisionedResourceMetadataObject,
  Maybe,
  SortDirection,
  FilterOption,
} from '@css-xp/graphtypes/src/generated-types-new';
import { emptyStateProp, errorStateProp, TableHeading, getSelectedColumns } from '@css-xp/ppc-common';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@css-xp/ppc-constants';
import { ColGroup, CustomDynamicResultTable, TableBody, TableHead, SearchResultColumn, HeadCell } from '@css-xp/base';
import './organisation-table.scss';

export const COLUMNS = {
  NAME: 'Name',
  ORGANISATION_ID: 'Organisation ID',
};

type ColumnList = HeadCell & SearchResultColumn;

const getHeaders = memoize(() => {
  return [
    {
      name: 'Name',
      content: COLUMNS.NAME,
    },
    {
      name: 'Organisation ID',
      content: COLUMNS.ORGANISATION_ID,
    },
  ];
});

export interface OrganisationTableProps {
  dataState: {
    data: Maybe<ProvisionedResourceMetadataObject>[];
    loading: boolean;
    refetch: any;
    error: string | undefined;
    totalCount: number;
  };
  transactionAccountId: string;
  label?: string;
  defaultFilterOptions?: FilterOption;
}

export const OrganisationTable = ({
  dataState,
  transactionAccountId,
  label,
  defaultFilterOptions,
}: OrganisationTableProps) => {
  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHeaders());
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const { data, loading, refetch, error, totalCount } = dataState;
  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: (pageNumber - 1) * rowsPerPage,
          sort: {
            sortFields: ['id'],
            sortFieldsDirections: [SortDirection.DESC],
          },
        },
        filterOptions: defaultFilterOptions,
      });
    },
    [refetch, rowsPerPage]
  );

  const handleRowsChange = useCallback(
    (rowsCount: number) => {
      setRowsPerPage(rowsCount);
      setCurrentPage(DEFAULT_PAGE);
      refetch({
        searchOptions: {
          first: rowsCount,
          offset: 0,
          sort: {
            sortFields: ['id'],
            sortFieldsDirections: [SortDirection.DESC],
          },
        },
        filterOptions: defaultFilterOptions,
      });
    },
    [refetch]
  );
  const getRowData = (orgData: ProvisionedResourceMetadataObject) => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(columnsList);
    const rowData = columns.map((column) => {
      switch (column.name) {
        case COLUMNS.NAME:
          return (
            <td>
              <Link
                to={`${getRouteUrl(routePaths.organisationDetails, {
                  txaId: transactionAccountId,
                  organisationId: `${encodeURIComponent(orgData?.organisationId ?? '')}`,
                })}`}
              >
                {orgData?.organisationName}
              </Link>
            </td>
          );
        case COLUMNS.ORGANISATION_ID:
          return <td>{orgData?.organisationId}</td>;
      }
    });
    return rowData;
  };
  const renderOrgTable = (orgDetail: any) => {
    const children = (
      <>
        <ColGroup columns={getSelectedColumns(columnsList)} />
        <TableHead columns={getSelectedColumns(columnsList)} />
        <TableBody>
          {orgDetail?.map((orgData: ProvisionedResourceMetadataObject, index: number) => {
            return <tr key={index}>{getRowData(orgData)}</tr>;
          })}
        </TableBody>
      </>
    );
    return (
      <CustomDynamicResultTable
        className={'org-search'}
        beforeTable={<TableHeading>{label}</TableHeading>}
        noResults={data.length === 0}
        error={error}
        totalCount={totalCount}
        isLoading={loading}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowChange={handleRowsChange}
        isPaginationEnabled={totalCount > DEFAULT_PAGE_SIZE}
        emptyStateProps={emptyStateProp}
        errorStateProps={errorStateProp}
      >
        {children}
      </CustomDynamicResultTable>
    );
  };
  return (
    <div className="organisation-search">
      <div>{renderOrgTable(data)}</div>
    </div>
  );
};
